import { useCallback, useEffect, useRef } from 'react';
import { useSYS4110_R01Store } from './Store_SYS4110_R01';
import { CalendarDto } from 'src/dtos/calendar/calendarDto';
import { 
  getCalendarList, 
  createCalendar, 
  updateCalendar, 
  deleteCalendar 
} from './Api_SYS4110_R01';
import useUiuxStore from 'src/store/useUiuxStore';
import { useToast } from 'src/hooks/useToast';
import { showSuccess } from 'src/utils/toast/toastUtil';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { usePrinterSettingStore } from 'src/store/usePrinterSettingStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import qz from 'qz-tray';

interface ContentProps {
  setMessage: (message: string) => void; 
}

export const Content_SYS4110_R01 = ({ setMessage }: ContentProps) => {
  const hasFetchedInitialEventsRef = useRef(false);
  const { success } = useToast();
  const { print } = useQzPrint();
  const getPrinter = usePrinterSettingStore((state) => state.getPrinter);
  const {
    currentDate,
    events,
    setEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    setSelectedEvent,
    setEventModalOpen,
    selectedCompCd,
    initData,
    setSelectedCompCd
  } = useSYS4110_R01Store();
  const previousYearMonthRef = useRef(
    `${currentDate.getFullYear()}-${currentDate.getMonth()}`
  );
  
  const { compCdList } = useCommonCodeStore();
  const { setLoadingSpinner } = useLoadingSpinnerStore();

  // 캘린더 목록 조회
  const handleFetchEvents = async () => {
    try {
      setLoadingSpinner(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      // 월의 첫날
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      
      // 월의 마지막날 (정확히 계산)
      const lastDay = new Date(year, month, 0).getDate(); // 0을 넣으면 이전 달의 마지막 날
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      // 백엔드 API에 맞게 검색 조건 전달
      const searchParams: any = {
        startDate,
        endDate,
        holidayYn: 'Y' // 휴일만 조회
      };
      
      // 선택된 사업장이 있으면 검색 조건에 추가
      if (selectedCompCd) {
        searchParams.compCd = selectedCompCd;
      }
      
    //   console.log('API 호출 파라미터:', searchParams);
      const response = await getCalendarList(searchParams);
      
      setEvents(response.data.data);
      setMessage(`${response.data.data.length}개의 일정을 조회했습니다.`);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error('캘린더 조회 실패:', error);
      setMessage('캘린더 조회에 실패했습니다.');
    } finally {
      setLoadingSpinner(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const initContainerData = useCallback(async () => {
    initData();
    setMessage("");
  }, [initData, setMessage]);

  // 일정 등록
  const handleCreateEvent = async (eventData: CalendarDto) => {
    try {
      setLoadingSpinner(true);
      const response = await createCalendar(eventData);
      addEvent(response.data.data);
      showSuccess('일정이 성공적으로 등록되었습니다.' );
      setMessage('일정이 성공적으로 등록되었습니다.');
    } catch (error) {
      console.error('일정 등록 실패:', error);
      setMessage('일정 등록에 실패했습니다.');
    } finally {
      setLoadingSpinner(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // 일정 수정
  const handleUpdateEvent = async (event: CalendarDto) => {
    try {
      setLoadingSpinner(true);
      const response = await updateCalendar(event);
      updateEvent(response.data.data);
      showSuccess('일정이 성공적으로 수정되었습니다.' );
      setMessage('일정이 성공적으로 수정되었습니다.');
    } catch (error) {
      console.error('일정 수정 실패:', error);
      setMessage('일정 수정에 실패했습니다.');
    } finally {
      setLoadingSpinner(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // 일정 삭제
  const handleDeleteEvent = async (compCd: string, systemDay: string) => {
    try {
      setLoadingSpinner(true);
      // systemDay를 calendarDt로 변환하여 백엔드 API에 맞춤
      const calendarDt = typeof systemDay === 'string' ? systemDay.substring(0, 10) : systemDay;
      await deleteCalendar(compCd, calendarDt);
      deleteEvent(compCd, systemDay);
      showSuccess('일정이 성공적으로 삭제되었습니다.' );
      setMessage('일정이 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('일정 삭제 실패:', error);
      setMessage('일정 삭제에 실패했습니다.');
    } finally {
      setLoadingSpinner(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // 일정 클릭 핸들러
  const handleEventClick = (event: CalendarDto) => {
    setSelectedEvent(event);
    setEventModalOpen(true);
  };

  // 날짜 클릭 핸들러 (신규 일정 등록)
  const handleDateClick = (date: Date) => {
    setSelectedEvent(null);
    setEventModalOpen(true);
  };

  // 신규 일정 버튼 클릭
  const handleCreateButtonClick = () => {
    setSelectedEvent(null);
    setEventModalOpen(true);
  };

  // 모달 닫기
  const handleModalClose = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
  };

  // 오늘 버튼 클릭
  const handleTodayClick = () => {
    const today = new Date();
    useSYS4110_R01Store.getState().setCurrentDate(today);
    setMessage('오늘로 이동했습니다.');
    setTimeout(() => setMessage(''), 3000);
  };

  // 엑셀 내보내기
  const handleExcelExport = () => {
    try {
      const csvContent = [
        ['회사코드', '날짜', '휴일여부', '휴일명', '휴일타입', '비고'],
        ...events.map(event => [
          event.compCd,
          event.systemDay,
          event.holidayYn,
          event.holidayName,
          event.holidayType,
          event.remark
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `calendar_events_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setMessage('일정 목록이 엑셀 파일로 내보내졌습니다.');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('엑셀 내보내기 실패:', error);
      setMessage('엑셀 내보내기에 실패했습니다.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // 사업장별 데이터 조회 (새로운 함수)
  const handleFetchEventsByCompCd = useCallback(async (compCd: string, showLoading = true) => {
    try {
      if (showLoading) {
        setLoadingSpinner(true);
      }
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      // 월의 첫날
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      
      // 월의 마지막날 (정확히 계산)
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      // 백엔드 API에 맞게 검색 조건 전달
      const searchParams: any = {
        startDate,
        endDate,
        holidayYn: 'Y'
      };
      
      // 전달받은 사업장 코드 사용
      if (compCd) {
        searchParams.compCd = compCd;
      }
      
      const response = await getCalendarList(searchParams);
      
      setEvents(response.data.data);
      setMessage(`${response.data.data.length}개의 일정을 조회했습니다.`);
    } catch (error) {
      console.error('캘린더 조회 실패:', error);
      setMessage('캘린더 조회에 실패했습니다.');
    } finally {
      if (showLoading) {
        setLoadingSpinner(false);
      }
      setTimeout(() => setMessage(''), 5000);
    }
  }, [currentDate, setEvents, setLoadingSpinner, setMessage]);

  // 사업장 초기 선택 (useEffect)
  useEffect(() => {
    if (hasFetchedInitialEventsRef.current || compCdList.length === 0) {
      return;
    }

    const initialCompCd = selectedCompCd || compCdList[0].compCd;
    hasFetchedInitialEventsRef.current = true;

    if (!selectedCompCd) {
      setSelectedCompCd(initialCompCd);
    }

    void handleFetchEventsByCompCd(initialCompCd);
  }, [compCdList, handleFetchEventsByCompCd, selectedCompCd, setSelectedCompCd]);

  // 월 이동 시 현재 선택한 사업장의 해당 월 일정을 자동 조회
  useEffect(() => {
    const currentYearMonth = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;

    if (previousYearMonthRef.current === currentYearMonth) {
      return;
    }

    previousYearMonthRef.current = currentYearMonth;

    if (!hasFetchedInitialEventsRef.current || !selectedCompCd) {
      return;
    }

    void handleFetchEventsByCompCd(selectedCompCd);
  }, [currentDate, handleFetchEventsByCompCd, selectedCompCd]);

  // 출력 핸들러
  const handlePrintClick = useCallback(async () => {
    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>캘린더 출력</title>
          <style>
            body { margin: 0; padding: 16px; }
          </style>
        </head>
        <body>${document.body.innerHTML}</body>
      </html>
    `;

    const openBrowserPrintWindow = () => {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        setMessage("팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
      }, 300);
    };

    const reportPrinter = getPrinter("report1");
    if (qz.websocket.isActive() && reportPrinter) {
      const ok = await print({ docType: "report1", data: html, dataType: "html" });
      if (!ok) {
        openBrowserPrintWindow();
      }
      return;
    }

    openBrowserPrintWindow();
  }, [getPrinter, print, setMessage]);

  // 사업장 변경 핸들러
  const handleCompCdChange = (compCd: string) => {
    console.log('사업장 변경됨:', compCd);
    setSelectedCompCd(compCd);
    // 사업장 변경 시 자동 조회하지 않음 - 조회 버튼 클릭 시에만 조회
  };

  return {
    handleFetchEvents,
    handleFetchEventsByCompCd,
	initContainerData,
    handleCreateEvent,
    handleUpdateEvent,
    handleDeleteEvent,
    handleEventClick,
    handleDateClick,
    handleCreateButtonClick,
    handleModalClose,
    handleTodayClick,
    handleExcelExport,
    handlePrintClick,
    handleCompCdChange,
  };
};
